import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLoading } from "../../../redux/ducks/Loading";
import { setTimesheetApproval } from "../../../redux/ducks/Section";
import { t } from "i18next";
import { Table, Row } from "antd";
import { EditOutlined, DownloadOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import CostCentresDashboard from "../components/CostCentresDashboard";
import { exportFile, formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { paginationDefaults } from "../../../utils/properties";
import { get } from "lodash";
import TimesheetApprovalApi from "../../../services/api/TimesheetApprovalApi";
import TimesheetPortal from "../../../services/api/TimesheetPortal";
import SettingsApi from "../../../services/api/Settings";

const TimesheetApproval = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const choose = useSelector((state) => get(state.sectionDuck, "timesheetApproval", {}));
  const [state, setState] = useState({
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    tableContent: [],
    downloadLoading: false,
  });

  useEffect(() => {
    getCurrentSettings();
  }, []);

  const getCurrentSettings = async () => {
    dispatch(setLoading(true));
    const settings = await SettingsApi.getSettings();
    if (!settings.error && settings.data) {
      dispatch(setTimesheetApproval({ timesheetApproval: get(settings, "data.timesheetApprover", null) }));
      dataList(state.pagination.currentPage, get(settings, "data.timesheetApprover", null));
    } else {
      showError(get(settings, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const dataList = async (page = state.pagination.currentPage, choose) => {
    dispatch(setLoading(true));
    let data = null;
    if (choose === "PM") {
      data = await TimesheetPortal.listProjectApproval(
        `page=${page - 1}&size=${state.pagination.size}&sort=pmName;asc,projectId;asc`
      );
    } else {
      data = await TimesheetApprovalApi.listEmployeeApproval(
        `page=${page - 1}&size=${state.pagination.size}&sort=name;asc,surname;asc`
      );
    }
    if (!data.error) {
      data.data.forEach((item, i) => {
        item.key = i; // This is necessary to remove warning in console for unique key row
        if (choose === "PM") {
          item.description = `${item.projectId} | ${item.projectName}`;
        } else {
          item.description = `${item.name} ${item.surname}`;
        }
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(data, "data", []),
        pagination: {
          currentPage: data.currentPage ? data.currentPage + 1 : paginationDefaults.page,
          size: get(data, "pageSize", paginationDefaults.size),
          totalItems: get(data, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(data.data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columnsResp_CDC = [
    {
      title: t("timesheet.approval.name"),
      dataIndex: "description",
      key: "name",
    },
    {
      title: t("timesheet.approval.monthAmount"), // ore totali = approvate + da approvare
      key: "monthAmountTotal",
      render: (record) =>
        formatNumber({
          value: record.monthAmountApproved + record.monthAmount,
          suffix: "HOUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    },
    {
      title: t("timesheet.approval.monthAmountApproved"),
      dataIndex: "monthAmountApproved", // ore già approvate
      key: "monthAmountApproved",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.monthAmountWaitingApproval"),
      dataIndex: "monthAmount", // ore da approvare
      key: "monthAmountWaitingApproval",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("general.actions"),
      fixed: "right",
      width: 80,
      render: (data) => {
        return (
          <div key={data.key} className="justify-center">
            <div onClick={() => navigate(`resp-cdc/${data.cid}`, { state: { name: `${data.name} ${data.surname}` } })}>
              <EditOutlined className={"icon-small"} />
            </div>
          </div>
        );
      },
    },
  ];

  const columnsPM = [
    {
      title: t("timesheet.approval.pmName"),
      dataIndex: "pmName",
      key: "pmName",
    },
    {
      title: t("timesheet.approval.projects"),
      dataIndex: "description",
      key: "project",
    },
    {
      title: t("timesheet.approval.monthAmount"), // ore totali = approvate + da approvare
      // dataIndex: "totalHour",
      key: "monthAmountTotal",
      render: (record) =>
        formatNumber({
          value: record.monthAmountApproved + record.monthAmount,
          suffix: "HOUR",
          lang: currentLanguage,
          decimalScale: 0,
        }),
    },
    {
      title: t("timesheet.approval.monthAmountApproved"),
      dataIndex: "monthAmountApproved", // ore già approvate
      key: "monthAmountApproved",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("timesheet.approval.monthAmountWaitingApproval"),
      dataIndex: "monthAmount", // ore da approvare
      key: "monthAmountWaitingApproval",
      render: (amount) => formatNumber({ value: amount, suffix: "HOUR", lang: currentLanguage, decimalScale: 0 }),
    },
    {
      title: t("general.actions"),
      fixed: "right",
      width: 80,
      render: (data) => {
        return (
          <div key={data.key} className="justify-center">
            <div onClick={() => navigate(`pm/${data.projectId}`)}>
              <EditOutlined className={"icon-small"} />
            </div>
          </div>
        );
      },
    },
  ];

  const handleDownload = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    let file = null;
    if (choose === "PM") {
      file = await TimesheetPortal.downloadTimesheetApprovalList();
    } else {
      file = await TimesheetApprovalApi.downloadTimesheetApprovalList();
    }
    if (!file.error) {
      const blob = new Blob([file]);
      exportFile(blob, `timesheet approval.csv`);
      showSuccess();
    } else {
      showError(get(file, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      {choose === "TIMESHEET_APPROVER" && <CostCentresDashboard />}
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={handleDownload}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={choose === "PM" ? columnsPM : columnsResp_CDC}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(page, choose);
          },
        }}
      />
    </>
  );
};

export default TimesheetApproval;
